// Libellé + explication du badge de désactivation automatique.
//
// Partagé par TradingAccountsPage et AccountDetailPage : les deux affichaient
// le même ternaire binaire (broker_gone / sinon drawdown), qui rangeait tout
// motif inconnu dans « drawdown dépassé ». L'ajout d'un troisième motif
// (challenge validé) rendait cette forme dangereuse : un challenge réussi se
// serait affiché « Drawdown dépassé ».
//
// Module PUR : reçoit les chaînes déjà traduites, ne connaît pas i18n.

import { CLOSURE_REASON } from "./accountClosureReason";

export interface DeactivationLabels {
  brokerGone: string;
  brokerGoneHint: string;
  challengeCompleted: string;
  challengeCompletedHint: string;
  drawdown: string;
  drawdownHint: string;
}

export interface DeactivationBadge {
  label: string;
  hint: string;
}

/** Forme minimale attendue du bundle de traductions (évite d'importer le type
 *  complet Translations dans un module pur). */
interface TranslationsLike {
  tradingAccounts: DeactivationLabelKeys;
}

interface DeactivationLabelKeys {
  deactivatedBrokerGone: string;
  deactivatedBrokerGoneHint: string;
  deactivatedChallengeCompleted: string;
  deactivatedChallengeCompletedHint: string;
  deactivatedDrawdown: string;
  deactivatedDrawdownHint: string;
}

/** Adapte le bundle i18n à la forme attendue par deactivationBadge. Défini une
 *  seule fois pour que les deux pages qui affichent ce badge ne divergent pas. */
export function deactivationLabels(t: TranslationsLike): DeactivationLabels {
  return {
    brokerGone: t.tradingAccounts.deactivatedBrokerGone,
    brokerGoneHint: t.tradingAccounts.deactivatedBrokerGoneHint,
    challengeCompleted: t.tradingAccounts.deactivatedChallengeCompleted,
    challengeCompletedHint: t.tradingAccounts.deactivatedChallengeCompletedHint,
    drawdown: t.tradingAccounts.deactivatedDrawdown,
    drawdownHint: t.tradingAccounts.deactivatedDrawdownHint,
  };
}

/**
 * Traduit un `Account.deactivatedReason` en badge affichable.
 *
 * Trois cas :
 *  - motif connu       -> libellé traduit + explication ;
 *  - motif LIBRE       -> affiché tel quel, sans explication. `updateAccount`
 *    accepte en effet une chaîne libre (max 50) saisie par l'utilisateur, et
 *    le seed preprod en écrit ("Drawdown maximum atteint"). Les ranger dans le
 *    `default` du ternaire d'origine les affichait comme « Drawdown dépassé » ;
 *    les ignorer ferait disparaître un badge qui s'affichait avant. On montre
 *    donc le texte de l'utilisateur, qui est déjà lisible ;
 *  - absent/vide       -> null, aucun badge.
 */
export function deactivationBadge(
  reason: string | null | undefined,
  labels: DeactivationLabels,
): DeactivationBadge | null {
  switch (reason) {
    case CLOSURE_REASON.BROKER_GONE:
      return { label: labels.brokerGone, hint: labels.brokerGoneHint };
    case CLOSURE_REASON.CHALLENGE_COMPLETED:
      return { label: labels.challengeCompleted, hint: labels.challengeCompletedHint };
    case CLOSURE_REASON.DRAWDOWN_BREACH:
      return { label: labels.drawdown, hint: labels.drawdownHint };
    default: {
      const free = typeof reason === "string" ? reason.trim() : "";
      return free === "" ? null : { label: free, hint: free };
    }
  }
}
